/**
 * Angular InputField Component
 *
 * Migrated from the AngularJS `inputField` attribute directive
 * (apps/b2b-ui/common/directives/inputField/).
 *
 * Renders a complete form field — label + optional addon/icon + input + clear
 * button + error message. Implements ControlValueAccessor for use with
 * `[(ngModel)]` / `formControl`, and exposes a `[(model)]` two-way binding for
 * use outside Forms and from the AngularJS downgrade.
 *
 * Notes on parity with the legacy directive:
 * - The AJS module declared `fieldFocus`, `capitalize` and `numeric` directive
 *   deps; only `fieldFocus` and `numeric` had template-level effects. They are
 *   re-implemented inline (focus on AfterViewInit; non-digit guard via
 *   `(beforeinput)`). `capitalize` was unused in the template and is dropped.
 * - The original `clear()` used jQuery to refocus the input; we use ViewChild.
 *
 * Usage in Angular templates:
 *   <acme-input-field name="email" type="email" label="Email"
 *                     [(ngModel)]="email"
 *                     [validTest]="form.email.valid"
 *                     errorMessage="Please enter a valid email">
 *   </acme-input-field>
 *
 * Usage in AngularJS templates (after downgrade):
 *   <acme-input-field name="email" type="email" label="Email"
 *                     model="$ctrl.email" on-model-change="$ctrl.email = $event"
 *                     valid-test="$ctrl.isEmailValid"
 *                     error-message="Please enter a valid email">
 *   </acme-input-field>
 */
import {
  AfterViewInit, booleanAttribute, Component, computed, ElementRef,
  EventEmitter, forwardRef, Input, numberAttribute, Output,
  signal, ViewChild,
} from '@angular/core';
import { NgClass, NgIf } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

let uniqueId = 0;

/**
 * The host element acts as the wrapper (replaces the outer `<div class="inputField …">`
 * the legacy AJS directive emitted with `replace: true`), so layout classes
 * passed in by consumers stay on the same element as `inputField` /
 * `form-group` / `panel-form-group`, preserving the legacy CSS contract
 * (e.g. `@media (min-width: 992px) { .panel-form-group label { float: left; … } }`).
 *
 * All host classes are exposed as `[class.x]` bindings (not a static `'class'`
 * entry) so they are applied via `classList.add/remove` and DO NOT clobber any
 * `class="…"` provided by the consumer — critical under `downgradeComponent`,
 * where AJS sets the element's class attribute before Angular initializes the
 * host bindings.
 */
@Component({
  selector: 'acme-input-field',
  standalone: true,
  imports: [NgIf, NgClass],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => InputFieldComponent), multi: true },
  ],
  template: require('./input-field.component.html'),
  styles: [require('./input-field.component.less')],
  host: {
    '[class.inputField]': 'true',
    '[class.form-group]': '!isPanelGroup',
    '[class.panel-form-group]': 'isPanelGroup',
    '[class.constrain]': 'isConstrained',
    '[class.input-field-error]': 'isInvalid()',
    '[class.generic-input-error]': 'isInvalid()',
  },
})
export class InputFieldComponent implements ControlValueAccessor, AfterViewInit {
  @Input() type = 'text';
  @Input() addon = '';
  @Input() icon = '';
  @Input() errorMessage = '';
  @Input() placeholderValue = '';
  @Input() maxLength: number | null = null;
  @Input({ transform: numberAttribute }) fieldFocusTimeout = 0;
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input({ transform: booleanAttribute }) useCancel = false;
  @Input({ transform: booleanAttribute }) setFieldFocus = false;
  @Input({ transform: booleanAttribute }) hideLabel = false;
  @Input({ transform: booleanAttribute }) required = false;
  @Input({ transform: booleanAttribute }) useFullWidth = false;
  @Input({ transform: booleanAttribute }) numericOnly = false;

  private readonly fallbackName = `acme-input-field-${++uniqueId}`;

  private readonly _name = signal('');
  @Input()
  get name(): string { return this._name() || this.fallbackName; }
  set name(value: string) { this._name.set(value ?? ''); }

  private readonly _label = signal('');
  @Input()
  get label(): string { return this._label(); }
  set label(value: string) { this._label.set(value ?? ''); }

  private readonly _hasAddon = signal(false);
  @Input({ transform: booleanAttribute })
  get hasAddon(): boolean { return this._hasAddon(); }
  set hasAddon(value: boolean) { this._hasAddon.set(value); }

  private readonly _isPanelGroup = signal(false);
  @Input({ transform: booleanAttribute })
  get isPanelGroup(): boolean { return this._isPanelGroup(); }
  set isPanelGroup(value: boolean) { this._isPanelGroup.set(value); }

  private readonly _validTest = signal(true);
  @Input({ transform: booleanAttribute })
  get validTest(): boolean { return this._validTest(); }
  set validTest(value: boolean) { this._validTest.set(value); }

  private readonly _value = signal('');
  @Input()
  get model(): string { return this._value(); }
  set model(v: string) { this._value.set(v ?? ''); }
  @Output() modelChange = new EventEmitter<string>();

  readonly value = this._value.asReadonly();
  readonly groupTypeClass = computed(() => this._isPanelGroup() ? 'panel-form-group' : 'form-group');
  readonly isInvalid = computed(() => !this._validTest());
  readonly displayLabel = computed(() => this._label() || this.placeholderValue || '');
  readonly inputId = computed(() =>
    `input-${this._name() || this.fallbackName}${this._hasAddon() ? '-addon' : ''}`);
  readonly errorId = computed(() =>
    `${this._name() || this.fallbackName}-error-message${this._hasAddon() ? '-addon' : ''}`);

  get isConstrained(): boolean { return this.maxLength != null && !this.useFullWidth; }
  get hostInvalid(): boolean { return this.isInvalid(); }

  @ViewChild('inputEl') inputEl?: ElementRef<HTMLInputElement>;

  private onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  ngAfterViewInit(): void {
    if (this.setFieldFocus) {
      setTimeout(() => this.inputEl?.nativeElement.focus(), this.fieldFocusTimeout);
    }
  }

  onInput(event: Event): void {
    const v = (event.target as HTMLInputElement).value;
    this._value.set(v);
    this.modelChange.emit(v);
    this.onChange(v);
  }

  onBlur(): void { this.onTouched(); }

  onBeforeInput(event: InputEvent): void {
    if (!this.numericOnly) { return; }
    const data = event.data;
    if (data != null && !/^[0-9]+$/.test(data)) {
      event.preventDefault();
    }
  }

  clear(): void {
    this._value.set('');
    this.modelChange.emit('');
    this.onChange('');
    this.inputEl?.nativeElement.focus();
  }

  writeValue(value: string): void { this._value.set(value ?? ''); }
  registerOnChange(fn: (value: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }
}
